import { getMomentFromCurrentFullHour } from 'helpers/dateTime.helper/dateTime.helper'

import moment from 'moment'
import Datetime from 'utils/datetime/datetime'

// Helper function to count expected datapoints between two timestamps
function countExpectedDataPoints(startTime: ISODateTime, endTime: ISODateTime): number {
  if (Datetime.isBefore(endTime, startTime)) {
    return 0
  }
  return Datetime.getHoursBetween(startTime, endTime).length
}

// Helper function to split missing datapoints into historic and future periods
function splitMissingDataPointsByPeriod({
  startTime,
  endTime,
  times,
  values,
  historicFutureMarker,
}: {
  startTime: ISODateTime
  endTime: ISODateTime
  times: ISODateTime[] | number[]
  values: (number | null)[]
  historicFutureMarker: ISODateTime
}): {
  historicMissing: number
  futureMissing: number
  historicExpected: number
  futureExpected: number
  historicActual: number
  futureActual: number
} {
  // If entire range is before marker (all historic)
  if (Datetime.isBeforeOrEqual(endTime, historicFutureMarker)) {
    const expected = countExpectedDataPoints(startTime, endTime)
    // Count actual non-null values within the time range
    let actual = 0
    for (let i = 0; i < times.length; i++) {
      if (
        values[i] !== null &&
        Datetime.isAfterOrEqual(times[i], startTime) &&
        Datetime.isBeforeOrEqual(times[i], endTime)
      ) {
        actual++
      }
    }
    const missing = Math.max(0, expected - actual)
    return {
      historicMissing: missing,
      futureMissing: 0,
      historicExpected: expected,
      futureExpected: 0,
      historicActual: actual,
      futureActual: 0,
    }
  }

  // If entire range is after marker (all future)
  if (Datetime.isAfter(startTime, historicFutureMarker)) {
    const expected = countExpectedDataPoints(startTime, endTime)
    let actual = 0
    for (let i = 0; i < times.length; i++) {
      if (
        values[i] !== null &&
        Datetime.isAfterOrEqual(times[i], startTime) &&
        Datetime.isBeforeOrEqual(times[i], endTime)
      ) {
        actual++
      }
    }
    const missing = Math.max(0, expected - actual)
    return {
      historicMissing: 0,
      futureMissing: missing,
      historicExpected: 0,
      futureExpected: expected,
      historicActual: 0,
      futureActual: actual,
    }
  }

  // Range spans both historic and future
  const historicExpected = countExpectedDataPoints(startTime, historicFutureMarker)
  const totalExpected = countExpectedDataPoints(startTime, endTime)
  const futureExpected = totalExpected - historicExpected // Avoid double-counting the boundary hour

  // Count actual non-null values in historic vs future periods based on their timestamps
  let historicActual = 0
  let futureActual = 0

  for (let i = 0; i < times.length; i++) {
    const time = times[i]
    const value = values[i]

    // Skip if value is null
    if (value === null) continue

    // Skip if time is outside the expected range
    if (Datetime.isBefore(time, startTime) || Datetime.isAfter(time, endTime)) continue

    // Categorize as historic or future based on actual timestamp
    // Historic includes timestamps up to and including the marker
    if (Datetime.isBeforeOrEqual(time, historicFutureMarker)) {
      historicActual++
    } else {
      futureActual++
    }
  }

  return {
    historicMissing: Math.max(0, historicExpected - historicActual),
    futureMissing: Math.max(0, futureExpected - futureActual),
    historicExpected,
    futureExpected,
    historicActual,
    futureActual,
  }
}

export function calculateDataQuality({
  datasets,
  startTime,
  datasetMinHorizonOffsets = {},
  currentTime, // Optional: allows deterministic time-based calculations (e.g., tests). If not provided, uses actual current time
}: {
  datasets: Array<Pick<Dataset, 'return_id' | 'values' | 'times'>>
  startTime: ISODateTime
  datasetMinHorizonOffsets?: Record<string, number>
  currentTime?: ISODateTime
}): {
  historicMissing: number
  futureMissing: number
  historicExpected: number
  futureExpected: number
  historicActual: number
  futureActual: number
} {
  // Default expectation for historical data (up to current hour, offset -1)
  const defaultEndTime = currentTime
    ? Datetime.toISOString(moment(currentTime).subtract(1, 'hour').startOf('hour'))
    : Datetime.toISOString(getMomentFromCurrentFullHour(-1))

  let totalHistoricActual = 0
  let totalFutureActual = 0
  let totalHistoricMissing = 0
  let totalFutureMissing = 0
  let totalHistoricExpected = 0
  let totalFutureExpected = 0

  datasets.forEach((dataset) => {
    const horizonOffset = datasetMinHorizonOffsets[dataset.return_id]
    let datasetEndTime: ISODateTime

    if (horizonOffset !== undefined) {
      // Calculate expected data points based on custom horizon offset
      const customEndTime = currentTime
        ? Datetime.toISOString(moment(currentTime).add(horizonOffset, 'hours').startOf('hour'))
        : Datetime.toISOString(getMomentFromCurrentFullHour(horizonOffset))

      datasetEndTime = customEndTime
    } else {
      // Use default behavior (historical data up to current hour -1)
      datasetEndTime = defaultEndTime
    }

    // Calculate missing datapoints split by historic/future using actual timestamps
    const { historicMissing, futureMissing, historicExpected, futureExpected, historicActual, futureActual } =
      splitMissingDataPointsByPeriod({
        startTime,
        endTime: datasetEndTime,
        times: dataset.times,
        values: dataset.values,
        historicFutureMarker: defaultEndTime,
      })

    totalHistoricActual += historicActual
    totalFutureActual += futureActual
    totalHistoricMissing += historicMissing
    totalFutureMissing += futureMissing
    totalHistoricExpected += historicExpected
    totalFutureExpected += futureExpected
  })

  return {
    historicMissing: totalHistoricMissing,
    futureMissing: totalFutureMissing,
    historicExpected: totalHistoricExpected,
    futureExpected: totalFutureExpected,
    historicActual: totalHistoricActual,
    futureActual: totalFutureActual,
  }
}

export function getTimeRange({
  offsetStartTimeInHours,
  offsetEndTimeInHours,
  minutesAfterHourThreshold = 10, // If the current time is HH:00 - HH:10, then we want to check 1h back and not this current hour
}: {
  offsetStartTimeInHours: number // integer. Negative values are before now
  offsetEndTimeInHours: number
  minutesAfterHourThreshold?: number
}): {
  startTime: ISODateTime
  endTime: ISODateTime
  currentTime: ISODateTime
} {
  let startMoment = getMomentFromCurrentFullHour(offsetStartTimeInHours)
  let endMoment = getMomentFromCurrentFullHour(offsetEndTimeInHours)
  let currentMoment = moment()

  // Adjust endTime if within the minutesAfterHourThreshold
  if (minutesAfterHourThreshold > 0 && currentMoment.minutes() <= minutesAfterHourThreshold) {
    startMoment = startMoment.subtract(1, 'hours')
    endMoment = endMoment.subtract(1, 'hours')
    currentMoment = currentMoment.subtract(1, 'hours')
  }

  const startTime = Datetime.toISOString(startMoment)
  const endTime = Datetime.toISOString(endMoment)
  const currentTime = Datetime.toISOString(currentMoment)

  return {
    startTime,
    endTime,
    currentTime,
  }
}
